import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  Plus, 
  FileText, 
  CheckCircle, 
  Package, 
  Tag, 
  Truck, 
  ChevronsUpDown, 
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  AlertCircle,
  AlertTriangle,
  FileSpreadsheet,
  Download,
  Upload,
  CheckSquare,
  Square,
  XCircle,
  Ban,
  Search,
  RefreshCw,
  Loader2,
  Pencil,
  Play,
  MoreHorizontal,
  FileDown,
  Mail,
  Send,
  History,
  Eye,
  MailCheck,
  Save,
  Inbox,
  MailOpen,
  Paperclip
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { PageHeader, LoadingState, EmptyStateNoData, StatusBadge } from '@/components/design-system';

const PurchaseOrders = () => {
  const { user } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();

  // Deep-link: /purchase-orders?po=PO-XXXXXX abre e faz scroll até à encomenda
  useEffect(() => {
    const target = new URLSearchParams(location.search).get('po');
    if (!target || orders.length === 0) return;
    const po = orders.find(o => o.po_number === target);
    if (!po) return;
    setStatusFilter('all');
    setExpandedSuppliers(prev => ({ ...prev, [po.supplier || 'Sem Fornecedor']: true }));
    setExpandedPOs(prev => ({ ...prev, [target]: true }));
    setTimeout(() => {
      document.querySelector(`[data-po-anchor="${target}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 500);
  }, [orders, location.search]); // eslint-disable-line react-hooks/exhaustive-deps
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  // SAGE Modal state
  const [showSageModal, setShowSageModal] = useState(false);
  const [sagePoNumber, setSagePoNumber] = useState('');
  const [selectedPOForSage, setSelectedPOForSage] = useState(null);
  const [submittingSage, setSubmittingSage] = useState(false);
  const [expandedSuppliers, setExpandedSuppliers] = useState({});
  const [expandedPOs, setExpandedPOs] = useState({});
  const [sendingSagePO, setSendingSagePO] = useState({});
  const [syncingSage, setSyncingSage] = useState(false);
  const [cancellingPO, setCancellingPO] = useState(null); // po_number being cancelled
  const [probingPO, setProbingPO] = useState(null); // po_number being probed for satisfaction docs
  const [statusFilter, setStatusFilter] = useState('active'); // 'active' hides fully received
  const [globalSearch, setGlobalSearch] = useState(''); // Global search for PO, supplier, ENF, SKU
  const [editingPrice, setEditingPrice] = useState(null); // {itemId, currentPrice} for inline price edit
  const [editingQuantity, setEditingQuantity] = useState(null); // {itemId, currentQty} for inline quantity edit
  const [savingEdit, setSavingEdit] = useState(false);
  // PDF & Email states
  const [generatingPDF, setGeneratingPDF] = useState(null); // po_number
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailModalPO, setEmailModalPO] = useState(null);
  const [emailForm, setEmailForm] = useState({ to: '', cc: '', subject: '', message: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailHistory, setEmailHistory] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  // PDF Preview Modal
  const [showPDFPreview, setShowPDFPreview] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfPreviewPO, setPdfPreviewPO] = useState(null);
  const [loadingPDFPreview, setLoadingPDFPreview] = useState(false);
  // Email sent status per PO - now includes inbound replies
  const [emailSentStatus, setEmailSentStatus] = useState({}); // {po_number: {status: 'none'|'sent'|'new_reply'|'reply_read', sent_count, reply_count, has_unread}}
  // Inbound email viewer
  const [showInboundModal, setShowInboundModal] = useState(false);
  const [selectedInbound, setSelectedInbound] = useState(null);
  // Communication panel in PO detail
  const [communicationData, setCommunicationData] = useState(null);
  const [loadingCommunication, setLoadingCommunication] = useState(false);
  // Save supplier email
  const [savingSupplierEmail, setSavingSupplierEmail] = useState(false);
  
  // SAGE Sync state
  const [syncingFromSage, setSyncingFromSage] = useState({}); // {po_number: true}
  const [showSyncResultModal, setShowSyncResultModal] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [showSyncHistoryModal, setShowSyncHistoryModal] = useState(false);
  const [syncHistoryData, setSyncHistoryData] = useState(null);
  const [loadingSyncHistory, setLoadingSyncHistory] = useState(false);
  // Auto-sync pending warning
  const [showSyncPendingWarning, setShowSyncPendingWarning] = useState(false);
  const [syncPendingAction, setSyncPendingAction] = useState(null); // 'pdf' | 'email'
  const [formData, setFormData] = useState({
    product_id: '',
    quantity: 0,
    supplier: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Initialize expanded state when orders load - all collapsed by default
  useEffect(() => {
    if (orders.length > 0) {
      // Deep-link ?po=... mantém a expansão feita pelo efeito acima
      const target = new URLSearchParams(location.search).get('po');
      if (target && orders.some(o => o.po_number === target)) return;
      setExpandedSuppliers({});
      setExpandedPOs({});
    }
  }, [orders]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [ordersRes, productsRes] = await Promise.all([
        axios.get(`${API}/purchase-orders`),
        axios.get(`${API}/products?page_size=200`)
      ]);
      setOrders(ordersRes.data);
      setProducts(productsRes.data.items || []);
      
      // Fetch email sent status for integrated POs
      fetchEmailSentStatus(ordersRes.data);
    } catch (error) {
      toast.error('Falha ao carregar encomendas');
    } finally {
      setLoading(false);
    }
  };

  // Fetch email sent status for POs with SAGE integration using batch endpoint
  // No arbitrary limits - fetches status for ALL integrated POs in the current view
  const fetchEmailSentStatus = async (ordersList) => {
    const integratedPOs = ordersList.filter(o => o.sage_synced || o.sage_po_number);
    if (integratedPOs.length === 0) return;
    
    // Deduplicate by po_number
    const uniquePONumbers = [...new Set(integratedPOs.map(po => po.po_number))];
    
    try {
      // Use batch endpoint - single request for all POs, no arbitrary limits
      const res = await axios.post(`${API}/po-documents/batch/communication-status`, {
        po_numbers: uniquePONumbers
      });
      setEmailSentStatus(res.data);
    } catch (error) {
      console.error('Error fetching email status:', error);
    }
  };

  // Preview PDF in modal
  const handlePreviewPDF = async (poNumber) => {
    setLoadingPDFPreview(true);
    setPdfPreviewPO(poNumber);
    try {
      const response = await axios.get(`${API}/po-documents/${poNumber}/pdf`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPdfPreviewUrl(url);
      setShowPDFPreview(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar PDF');
    } finally {
      setLoadingPDFPreview(false);
    }
  };

  // Close PDF preview and cleanup
  const closePDFPreview = () => {
    setShowPDFPreview(false);
    if (pdfPreviewUrl) {
      window.URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
    }
    setPdfPreviewPO(null);
  };

  // Group orders by supplier, then by PO number
  const groupedOrders = useMemo(() => {
    // Group by supplier
    const bySupplier = {};
    orders.forEach(order => {
      const supplier = order.supplier || 'Sem Fornecedor';
      if (!bySupplier[supplier]) {
        bySupplier[supplier] = {
          name: supplier,
          orders: {},
          totalOrders: 0,
          totalItems: 0,
          pendingItems: 0,
          totalValue: 0,
          pendingValue: 0
        };
      }
      
      // Group by PO number within supplier
      const poNumber = order.po_number;
      if (!bySupplier[supplier].orders[poNumber]) {
        bySupplier[supplier].orders[poNumber] = {
          po_number: poNumber,
          items: [],
          totalQuantity: 0,
          receivedQuantity: 0,
          totalValue: 0,
          created_at: order.created_at,
          exported_at: order.exported_at,
          exported_by: order.exported_by,
          entered_sage_at: order.entered_sage_at,
          entered_sage_by: order.entered_sage_by,
          sage_po_number: order.sage_po_number,
          sage_synced: false,
          sage_sync_date: null,
          sage_vosso_numero: order.sage_vosso_numero,
          sage_satisfaction_docs: order.sage_satisfaction_docs || []
        };
        bySupplier[supplier].totalOrders++;
      }
      
      bySupplier[supplier].orders[poNumber].items.push(order);
      bySupplier[supplier].orders[poNumber].totalQuantity += order.quantity || 0;
      bySupplier[supplier].orders[poNumber].receivedQuantity += order.received_quantity || 0;
      bySupplier[supplier].orders[poNumber].totalValue += order.total_amount || 0;

      // Track sage_supplier_code coherence per PO (Sessão 25)
      // Source of truth: sage_supplier_code (not textual supplier name)
      if (!bySupplier[supplier].orders[poNumber].sageSupplierCodes) {
        bySupplier[supplier].orders[poNumber].sageSupplierCodes = new Set();
      }
      const code = (order.sage_supplier_code || '').trim();
      if (code) bySupplier[supplier].orders[poNumber].sageSupplierCodes.add(code);
      // Also track the supplier code at supplier-group level for display
      if (!bySupplier[supplier].sageSupplierCodes) {
        bySupplier[supplier].sageSupplierCodes = new Set();
      }
      if (code) bySupplier[supplier].sageSupplierCodes.add(code);

      // Update export/sage status
      if (order.exported_at) bySupplier[supplier].orders[poNumber].exported_at = order.exported_at;
      if (order.exported_by) bySupplier[supplier].orders[poNumber].exported_by = order.exported_by;
      if (order.entered_sage_at) bySupplier[supplier].orders[poNumber].entered_sage_at = order.entered_sage_at;
      if (order.entered_sage_by) bySupplier[supplier].orders[poNumber].entered_sage_by = order.entered_sage_by;
      if (order.sage_po_number) bySupplier[supplier].orders[poNumber].sage_po_number = order.sage_po_number;
      if (order.sage_synced) bySupplier[supplier].orders[poNumber].sage_synced = true;
      if (order.sage_sync_date) bySupplier[supplier].orders[poNumber].sage_sync_date = order.sage_sync_date;
      if (order.sage_vosso_numero) bySupplier[supplier].orders[poNumber].sage_vosso_numero = order.sage_vosso_numero;
      if (order.sage_satisfaction_docs && order.sage_satisfaction_docs.length) bySupplier[supplier].orders[poNumber].sage_satisfaction_docs = order.sage_satisfaction_docs;
      if (order.sage_integration_error) bySupplier[supplier].orders[poNumber].sage_integration_error = order.sage_integration_error;
      // SAGE sync fields
      if (order.sage_document_number) bySupplier[supplier].orders[poNumber].sage_document_number = order.sage_document_number;
      if (order.sage_document_serie) bySupplier[supplier].orders[poNumber].sage_document_serie = order.sage_document_serie;
      if (order.sage_last_content_sync) bySupplier[supplier].orders[poNumber].sage_last_content_sync = order.sage_last_content_sync;
      if (order.sage_integration_uncertain) bySupplier[supplier].orders[poNumber].sage_integration_uncertain = order.sage_integration_uncertain;
      
      // Update supplier totals — histórico exclui POs anuladas (só compras reais)
      bySupplier[supplier].totalItems++;
      if (order.status !== 'cancelled') {
        bySupplier[supplier].totalValue += order.total_amount || 0;
      }
    });

    // Calculate fulfillment status AFTER all items are grouped.
    // Sessão 2026-09-11 — Hierarquia de estado operacional única:
    //   1. anulada          → Cancelada
    //   2. satisfeita       → Totalmente recebida
    //   3. satisfeita_incompleta → SAGE fechou + parcial recebido
    //   4. nao_satisfeita   → SAGE fechou + nada recebido
    //   5. parcial          → Receção parcial em curso
    //   6. encomendada      → No SAGE, aguarda receção (NOVO)
    //   7. pronta           → Aprovada, pronta para SAGE (NOVO)
    //   8. pendente         → Rascunho (sem SAGE, não aprovada)
    Object.values(bySupplier).forEach(supplier => {
      Object.values(supplier.orders).forEach(po => {
        const isCancelled = po.items.some(i => i.status === 'cancelled');
        const isClosedUnsatisfied = po.items.some(i => i.status === 'closed_unsatisfied' || i.sage_doc_closed);
        const isInSage = po.sage_synced || po.entered_sage_at || po.sage_po_number;
        const isApproved = po.items.some(i => i.status === 'approved');

        if (isCancelled) {
          po.fulfillment = 'anulada';
        } else if (po.receivedQuantity >= po.totalQuantity && po.totalQuantity > 0) {
          po.fulfillment = 'satisfeita';
        } else if (isClosedUnsatisfied && po.receivedQuantity > 0) {
          // SAGE limpou pendente e houve receção parcial — ciclo terminou
          po.fulfillment = 'satisfeita_incompleta';
        } else if (isClosedUnsatisfied && po.receivedQuantity === 0) {
          po.fulfillment = 'nao_satisfeita';
        } else if (po.receivedQuantity > 0) {
          po.fulfillment = 'parcial';
        } else if (isInSage) {
          // PO está no SAGE mas ainda não recebeu nada
          po.fulfillment = 'encomendada';
        } else if (isApproved) {
          // PO aprovada mas ainda não enviada para SAGE
          po.fulfillment = 'pronta';
        } else {
          // Rascunho — sem SAGE, não aprovada
          po.fulfillment = 'pendente';
        }

        // POs com ciclo terminado não contam para pendingItems/pendingValue
        const isFinalState = ['satisfeita', 'satisfeita_incompleta', 'nao_satisfeita', 'anulada'].includes(po.fulfillment);
        if (!isFinalState) {
          supplier.pendingItems += po.items.length;
          supplier.pendingValue += po.totalValue;
        }
      });
    });

    // Apply filter
    const result = Object.values(bySupplier)
      .map(supplier => ({
        ...supplier,
        orders: Object.values(supplier.orders)
          .filter(po => {
            // Global search filter
            if (globalSearch) {
              const search = globalSearch.toLowerCase();
              const matchesPO = po.po_number.toLowerCase().includes(search);
              const matchesENF = po.sage_po_number?.toLowerCase().includes(search);
              const matchesSKU = po.items.some(i => i.sku?.toLowerCase().includes(search) || i.product_name?.toLowerCase().includes(search));
              if (!matchesPO && !matchesENF && !matchesSKU) return false;
            }
            
            // Status filter
            if (statusFilter === 'pendente') return po.fulfillment === 'pendente';
            if (statusFilter === 'pronta') return po.fulfillment === 'pronta';
            if (statusFilter === 'encomendada') return po.fulfillment === 'encomendada';
            if (statusFilter === 'parcial') return po.fulfillment === 'parcial';
            if (statusFilter === 'satisfeita') {
              return po.fulfillment === 'satisfeita'
                || po.fulfillment === 'satisfeita_incompleta'
                || po.fulfillment === 'nao_satisfeita';
            }
            if (statusFilter === 'anulada') return po.fulfillment === 'anulada';
            if (statusFilter === 'a_receber') {
              // Encomendadas ou parciais — aguardam mercadoria
              return po.fulfillment === 'encomendada' || po.fulfillment === 'parcial';
            }
            if (statusFilter === 'active') {
              return !['satisfeita', 'satisfeita_incompleta', 'nao_satisfeita', 'anulada'].includes(po.fulfillment);
            }
            return true; // 'all'
          })
          .sort((a, b) => {
            // POs dentro do fornecedor: mais recente primeiro
            return new Date(b.created_at) - new Date(a.created_at);
          })
      }))
      .filter(s => {
        // Supplier-level search filter
        if (globalSearch) {
          const search = globalSearch.toLowerCase();
          const matchesSupplier = s.name.toLowerCase().includes(search);
          const matchesSageCode = s.sageSupplierCodes && [...s.sageSupplierCodes].some(c => c.toLowerCase().includes(search));
          // If supplier matches or has matching POs, show it
          return matchesSupplier || matchesSageCode || s.orders.length > 0;
        }
        return s.orders.length > 0;
      })
      // Fornecedores ordenados A-Z pelo nome (case-insensitive, locale-aware PT)
      .sort((a, b) => {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB, 'pt', { sensitivity: 'base' });
      });

    return result;
  }, [orders, statusFilter, globalSearch]);

  // Summary stats — computed at PO level, not item level
  const stats = useMemo(() => {
    const allPOs = {};
    orders.forEach(o => {
      if (!allPOs[o.po_number]) allPOs[o.po_number] = {
        totalQty: 0, receivedQty: 0, totalValue: 0,
        sage_synced: false, sage_po_number: null, entered_sage_at: null,
        exported_at: null, hasCancelled: false, hasClosedUnsatisfied: false,
        hasApproved: false,
      };
      allPOs[o.po_number].totalQty += o.quantity || 0;
      allPOs[o.po_number].receivedQty += o.received_quantity || 0;
      allPOs[o.po_number].totalValue += o.total_amount || 0;
      if (o.sage_synced) allPOs[o.po_number].sage_synced = true;
      if (o.entered_sage_at) allPOs[o.po_number].entered_sage_at = o.entered_sage_at;
      if (o.sage_po_number) allPOs[o.po_number].sage_po_number = o.sage_po_number;
      if (o.exported_at) allPOs[o.po_number].exported_at = o.exported_at;
      if (o.status === 'cancelled') allPOs[o.po_number].hasCancelled = true;
      if (o.status === 'approved') allPOs[o.po_number].hasApproved = true;
      if (o.status === 'closed_unsatisfied' || o.sage_doc_closed) allPOs[o.po_number].hasClosedUnsatisfied = true;
    });

    let rascunhos = 0, prontas = 0, encomendadas = 0, parciais = 0, recebidas = 0, anuladas = 0;
    let totalValue = 0, aReceberValue = 0;

    Object.values(allPOs).forEach(po => {
      totalValue += po.totalValue;
      const isInSage = po.sage_synced || po.entered_sage_at || po.sage_po_number;
      
      if (po.hasCancelled) {
        anuladas++;
      } else if (po.receivedQty >= po.totalQty && po.totalQty > 0) {
        recebidas++;
      } else if (po.hasClosedUnsatisfied) {
        recebidas++;
      } else if (po.receivedQty > 0) {
        parciais++;
        aReceberValue += po.totalValue;
      } else if (isInSage) {
        encomendadas++;
        aReceberValue += po.totalValue;
      } else if (po.hasApproved) {
        prontas++;
        aReceberValue += po.totalValue;
      } else {
        rascunhos++;
        aReceberValue += po.totalValue;
      }
    });

    return {
      rascunhos, prontas, encomendadas, parciais, recebidas, anuladas,
      totalPOs: Object.keys(allPOs).length,
      totalValue, aReceberValue
    };
  }, [orders]);

  // Filter products based on search query
  const filteredProducts = useMemo(() => {
    if (!searchQuery) return products.slice(0, 20);
    const query = searchQuery.toLowerCase();
    return products
      .filter(p => 
        p.sku.toLowerCase().includes(query) || 
        p.name.toLowerCase().includes(query)
      )
      .slice(0, 20);
  }, [products, searchQuery]);

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setFormData({
      ...formData,
      product_id: product.id,
      supplier: product.supplier || ''
    });
    setSearchOpen(false);
    setSearchQuery('');
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!selectedProduct) {
      toast.error('Selecione um produto');
      return;
    }
    try {
      await axios.post(`${API}/purchase-orders`, {
        product_id: formData.product_id,
        quantity: Number(formData.quantity),
        supplier: formData.supplier || undefined
      });
      toast.success('Encomenda criada com sucesso');
      setShowCreateModal(false);
      setFormData({ product_id: '', quantity: 0, supplier: '' });
      setSelectedProduct(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Falha ao criar encomenda');
    }
  };

  const handleEstadoChange = async (orderId, newEstado) => {
    try {
      await axios.patch(`${API}/purchase-orders/${orderId}/status?status=${newEstado}`);
      toast.success('Estado atualizado');
      fetchData();
    } catch (error) {
      toast.error('Falha ao atualizar estado');
    }
  };

  const handleExportExcel = async (poNumber, supplierName) => {
    try {
      const response = await axios.get(`${API}/purchase-orders/${poNumber}/export-excel`, {
        responseType: 'blob',
        params: { armazem: '001', formato: 'xlsx' }
      });
      
      // Use supplier name in filename
      const safeSupplier = (supplierName || 'Fornecedor').replace(/[^\w\s.-]/g, '').trim().replace(/\s+/g, '_').substring(0, 30);
      const filename = `${safeSupplier}_${poNumber}_SAGE100C.xlsx`;
      
      // Create blob with correct MIME type
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      // Check if mobile device
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      if (isMobile && navigator.share && navigator.canShare) {
        // Use Web Share API for mobile if available
        try {
          const file = new File([blob], filename, { 
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
          });
          
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: `Encomenda ${poNumber}`,
              text: `Ficheiro Excel para SAGE 100C`
            });
            toast.success(`Ficheiro partilhado: ${filename}`);
            fetchData();
            return;
          }
        } catch (shareError) {
          console.log('Share API not available, falling back to download');
        }
      }
      
      // Standard download approach
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename; // This attribute forces download
      link.style.display = 'none';
      document.body.appendChild(link);
      
      // For iOS Safari, we need a small delay
      setTimeout(() => {
        link.click();
        // Clean up
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);
      }, 10);
      
      toast.success(`Ficheiro exportado: ${filename}`);
      fetchData();
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Falha ao exportar');
    }
  };

  const handleMarkSageEntered = async (poNumber) => {
    // Open modal to ask for SAGE PO number
    setSelectedPOForSage(poNumber);
    setSagePoNumber('');
    setShowSageModal(true);
  };

  const handleConfirmSageEntered = async () => {
    if (!selectedPOForSage || !sagePoNumber.trim()) {
      toast.error('Introduza o número da encomenda SAGE');
      return;
    }
    
    try {
      setSubmittingSage(true);
      await axios.post(`${API}/purchase-orders/${selectedPOForSage}/mark-sage-entered?sage_po_number=${encodeURIComponent(sagePoNumber.trim())}`);
      toast.success(
        <div className="flex flex-col">
          <span className="font-semibold">Encomenda registada no SAGE</span>
          <span className="text-sm">{selectedPOForSage} → {sagePoNumber}</span>
        </div>
      );
      setShowSageModal(false);
      setSelectedPOForSage(null);
      setSagePoNumber('');
      fetchData();
    } catch (error) {
      toast.error('Falha ao marcar como lançado no SAGE');
    } finally {
      setSubmittingSage(false);
    }
  };

  const handleUnmarkSageEntered = async (poNumber) => {
    try {
      await axios.delete(`${API}/purchase-orders/${poNumber}/unmark-sage-entered`);
      toast.success(`Marca SAGE removida da encomenda ${poNumber}`);
      fetchData();
    } catch (error) {
      toast.error('Falha ao remover marca');
    }
  };

  const handleSendPOToSage = async (poNumber) => {
    setSendingSagePO(prev => ({ ...prev, [poNumber]: true }));
    try {
      const response = await axios.post(`${API}/sage/create-purchase-order?po_number=${encodeURIComponent(poNumber)}`);
      const sageRef = response.data.sage_reference || response.data.message;
      if (response.data.already_sent) {
        toast.info(`${poNumber} já estava no SAGE: ${sageRef}`);
      } else {
        toast.success(`${poNumber} enviada ao SAGE: ${sageRef}`);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar ao SAGE');
    } finally {
      setSendingSagePO(prev => ({ ...prev, [poNumber]: false }));
    }
  };

  const handleSyncSageStatus = async () => {
    setSyncingSage(true);
    try {
      // Sync PO status of StockFlow-created POs with SAGE
      // Also trigger stock refresh in parallel so received items reflect updated current_stock
      const [poResp] = await Promise.all([
        axios.post(`${API}/sage/sync-po-status`),
        axios.post(`${API}/sage/sync`).catch(() => null), // best-effort, runs in background
      ]);
      const { synced, updated_items, errors } = poResp.data;
      
      if (updated_items > 0) {
        toast.success(`${updated_items} artigos actualizados em ${synced} encomendas`);
      } else {
        toast.info(`${synced} encomendas verificadas, sem alterações`);
      }
      if (errors && errors.length > 0) {
        errors.forEach(e => toast.error(e));
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao sincronizar com SAGE');
    } finally {
      setSyncingSage(false);
    }
  };

  const handleProbeSatisfactionDocs = async (poNumber) => {
    setProbingPO(poNumber);
    try {
      const response = await axios.post(
        `${API}/sage/sync-po-status?po_number=${encodeURIComponent(poNumber)}&find_satisfaction_docs=true`
      );
      const errors = response.data.errors;
      const found = response.data.satisfaction_docs_found ?? 0;
      if (errors && errors.length > 0) {
        errors.forEach(e => toast.error(e));
      } else if (found > 0) {
        toast.success(`${poNumber}: ${found} GEF/CCF encontrado${found > 1 ? 's' : ''} no SAGE`);
      } else {
        toast.info(`${poNumber}: nenhum GEF/CCF encontrado no SAGE para esta encomenda`);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao procurar GEF/CCF no SAGE');
    } finally {
      setProbingPO(null);
    }
  };

  const handleCancelPO = async (poNumber, hasSage) => {
    const isAdmin = user?.role === 'admin';
    let force = false;
    if (hasSage) {
      if (!isAdmin) {
        toast.error(
          `Encomenda ${poNumber} já foi integrada no SAGE. Anule directamente no SAGE para evitar inconsistências.`
        );
        return;
      }
      if (!window.confirm(
        `⚠️ ${poNumber} já está integrada no SAGE.\n\nAnular APENAS na app? O documento no SAGE mantém-se ativo — anula-o também no SAGE para evitar inconsistências.`
      )) return;
      force = true;
    } else if (!window.confirm(`Anular ${poNumber}?`)) {
      return;
    }

    setCancellingPO(poNumber);
    try {
      const response = await axios.post(`${API}/purchase-orders/${encodeURIComponent(poNumber)}/cancel`, {
        motivo: 'Anulado pelo utilizador',
        ...(force ? { force: true } : {}),
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao anular encomenda');
    } finally {
      setCancellingPO(null);
    }
  };

  // ---------- Inline Editing Functions ----------

  const handleInlinePriceEdit = async (itemId, newPrice) => {
    if (newPrice < 0) {
      toast.error('Preço não pode ser negativo');
      return;
    }
    setSavingEdit(true);
    try {
      await axios.patch(`${API}/purchase-orders/items/${itemId}/price?price=${newPrice}`);
      toast.success('Preço atualizado');
      setEditingPrice(null);
      fetchData();
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error(error.response.data.detail || 'Encomenda não editável');
      } else {
        toast.error('Erro ao atualizar preço');
      }
    } finally {
      setSavingEdit(false);
    }
  };

  const handleInlineQuantityEdit = async (itemId, newQty) => {
    if (newQty <= 0) {
      toast.error('Quantidade deve ser maior que zero');
      return;
    }
    setSavingEdit(true);
    try {
      await axios.patch(`${API}/purchase-orders/items/${itemId}/quantity?quantity=${newQty}`);
      toast.success('Quantidade atualizada');
      setEditingQuantity(null);
      fetchData();
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error(error.response.data.detail || 'Encomenda não editável');
      } else {
        toast.error('Erro ao atualizar quantidade');
      }
    } finally {
      setSavingEdit(false);
    }
  };

  const canEditPO = (po) => {
    // PO is editable if not SAGE-synced and no receptions
    return !po.sage_synced && !po.entered_sage_at && 
           (po.fulfillment === 'pendente' || po.status === 'pending' || po.status === 'approved');
  };

  const handleContinueDraft = (po) => {
    // Navigate to SupplierOrders with the PO data to continue editing
    const supplier = po.supplier || po.items?.[0]?.supplier;
    if (supplier) {
      navigate(`/supplier-orders?continue=${encodeURIComponent(po.po_number)}&supplier=${encodeURIComponent(supplier)}`);
    } else {
      toast.error('Fornecedor não identificado');
    }
  };

  // ========== PDF & Email Functions ==========

  const isPOIntegrated = (po) => {
    return !!(po.sage_synced || po.entered_sage_at || po.sage_po_number);
  };

  const handleDownloadPDF = async (poNumber) => {
    setGeneratingPDF(poNumber);
    try {
      const response = await axios.get(`${API}/po-documents/${poNumber}/pdf`, {
        responseType: 'blob'
      });
      
      // Get filename from Content-Disposition header or default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Encomenda_${poNumber}.pdf`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF descarregado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar PDF');
    } finally {
      setGeneratingPDF(null);
    }
  };

  const handleOpenEmailModal = async (po) => {
    // Check if content sync is pending/failed
    if (po.sage_content_sync_pending) {
      setEmailModalPO(po);
      setSyncPendingAction('email');
      setShowSyncPendingWarning(true);
      return;
    }
    
    setEmailModalPO(po);
    try {
      const response = await axios.get(`${API}/po-documents/${po.po_number}/email-defaults`);
      setEmailForm({
        to: response.data.supplier_email || '',
        cc: '',
        subject: response.data.default_subject,
        message: response.data.default_message,
        emailSource: response.data.email_source || 'sage'  // Track if managed by StockFlow
      });
      setShowEmailModal(true);
    } catch (error) {
      toast.error('Erro ao carregar dados para email');
    }
  };
  
  // Handle sync pending warning actions
  const handleSyncPendingSync = async () => {
    // Try to sync, then continue with action if successful
    const po = emailModalPO;
    if (!po) return;
    
    setShowSyncPendingWarning(false);
    await handleSyncFromSage(po.po_number);
    
    // Refresh data and recheck
    await fetchData();
  };
  
  const handleSyncPendingContinue = () => {
    // Continue with action despite pending sync
    setShowSyncPendingWarning(false);
    const action = syncPendingAction;
    const po = emailModalPO;
    
    if (action === 'email' && po) {
      // Force open email modal
      axios.get(`${API}/po-documents/${po.po_number}/email-defaults`)
        .then(response => {
          setEmailForm({
            to: response.data.supplier_email || '',
            cc: '',
            subject: response.data.default_subject,
            message: response.data.default_message,
            emailSource: response.data.email_source || 'sage'
          });
          setShowEmailModal(true);
        })
        .catch(() => toast.error('Erro ao carregar dados para email'));
    } else if (action === 'pdf' && po) {
      handleDownloadPDF(po.po_number);
    }
  };

  // Validate email address(es) - supports comma/semicolon separated
  const validateEmails = (emailString) => {
    if (!emailString || !emailString.trim()) return { valid: false, invalid: [] };
    
    // Parse multiple emails
    const emails = emailString
      .replace(/;/g, ',')
      .split(',')
      .map(e => e.trim())
      .filter(e => e);
    
    if (emails.length === 0) return { valid: false, invalid: [] };
    
    // Validate each email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalid = emails.filter(e => !emailRegex.test(e));
    
    return {
      valid: invalid.length === 0 && emails.length > 0,
      invalid: invalid,
      emails: emails
    };
  };

  const handleSendEmail = async () => {
    // Validate To - required, accepts multiple
    const toValidation = validateEmails(emailForm.to);
    if (!toValidation.valid) {
      if (toValidation.invalid.length > 0) {
        toast.error(`Email(s) "Para" inválido(s): ${toValidation.invalid.join(', ')}`);
      } else {
        toast.error('Email de destino inválido');
      }
      return;
    }
    
    // Validate CC - optional, accepts multiple
    let effectiveCc = emailForm.cc?.trim() || null;
    if (effectiveCc) {
      const ccValidation = validateEmails(effectiveCc);
      if (!ccValidation.valid && ccValidation.invalid.length > 0) {
        toast.error(`Email(s) CC inválido(s): ${ccValidation.invalid.join(', ')}`);
        return;
      }
      
      // Remove CC emails that are already in To (deduplication)
      const toSet = new Set(toValidation.emails.map(e => e.toLowerCase()));
      const uniqueCc = ccValidation.emails.filter(e => !toSet.has(e.toLowerCase()));
      effectiveCc = uniqueCc.length > 0 ? uniqueCc.join(', ') : null;
    }
    
    setSendingEmail(true);
    try {
      const response = await axios.post(`${API}/po-documents/${emailModalPO.po_number}/send-email`, {
        to: toValidation.emails.join(', '),  // Normalized format
        cc: effectiveCc,
        subject: emailForm.subject,
        message: emailForm.message
      });
      toast.success(response.data.message || 'Email enviado com sucesso');
      
      // Update local email sent status immediately with correct format
      setEmailSentStatus(prev => {
        const current = prev[emailModalPO.po_number] || { status: 'none', sent_count: 0, reply_count: 0, has_unread: false };
        return {
          ...prev,
          [emailModalPO.po_number]: {
            ...current,
            status: current.reply_count > 0 ? (current.has_unread ? 'new_reply' : 'reply_read') : 'sent',
            sent_count: (current.sent_count || 0) + 1,
            // Keep legacy format for backward compatibility
            sent: true,
            lastSentAt: new Date().toISOString(),
            sentTo: toValidation.emails.join(', '),
            count: (current.sent_count || 0) + 1
          }
        };
      });
      
      // Also refresh from server to ensure consistency (async, don't block UI)
      setTimeout(() => {
        fetchEmailSentStatus(orders);
      }, 1000);
      
      setShowEmailModal(false);
      setEmailModalPO(null);
    } catch (error) {
      console.error('Email send error:', error);
      const errorDetail = error.response?.data?.detail;
      if (typeof errorDetail === 'object' && errorDetail.msg) {
        // Pydantic validation error
        toast.error(`Erro de validação: ${errorDetail.msg}`);
      } else {
        toast.error(errorDetail || 'Erro ao enviar email');
      }
    } finally {
      setSendingEmail(false);
    }
  };

  const handleShowEmailHistory = async (poNumber) => {
    try {
      setLoadingCommunication(true);
      const response = await axios.get(`${API}/po-documents/${poNumber}/communication-status`);
      setCommunicationData(response.data);
      setShowHistoryModal(true);
    } catch (error) {
      toast.error('Erro ao carregar comunicações');
    } finally {
      setLoadingCommunication(false);
    }
  };

  // Mark inbound email as read
  const handleMarkInboundRead = async (emailId) => {
    try {
      await axios.post(`${API}/webhooks/inbound-emails/${emailId}/mark-read`);
      // Refresh communication data
      if (communicationData?.po_number) {
        const response = await axios.get(`${API}/po-documents/${communicationData.po_number}/communication-status`);
        setCommunicationData(response.data);
        // Also refresh the list status
        fetchEmailSentStatus(orders);
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  // Open inbound email detail
  const handleOpenInbound = async (inbound) => {
    setSelectedInbound(inbound);
    setShowInboundModal(true);
    // Mark as read if not already
    if (!inbound.read_at) {
      await handleMarkInboundRead(inbound.id);
    }
  };

  // Save supplier email as permanent contact
  const handleSaveSupplierEmail = async () => {
    if (!emailModalPO || !emailForm.to) return;
    
    const supplierCode = emailModalPO.sage_supplier_code || emailModalPO.supplier;
    if (!supplierCode) {
      toast.error('Código do fornecedor não disponível');
      return;
    }
    
    setSavingSupplierEmail(true);
    try {
      await axios.put(`${API}/suppliers/${encodeURIComponent(supplierCode)}/email`, {
        email: emailForm.to.trim()
      });
      toast.success('Email do fornecedor guardado com sucesso');
      // Update form to show it's now managed by StockFlow
      setEmailForm(prev => ({ ...prev, emailSource: 'stockflow' }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao guardar email');
    } finally {
      setSavingSupplierEmail(false);
    }
  };

  // =============== SAGE SYNC (SAGE → StockFlow) ===============
  
  // Check if PO can be synced from SAGE
  const canSyncFromSage = (po) => {
    // Must be integrated with SAGE
    if (!po.sage_synced) return false;
    // Must have valid SAGE reference
    if (!po.sage_document_number || !po.sage_document_serie) return false;
    // Cannot have any receptions (check total received across all items)
    if ((po.receivedQuantity || 0) > 0) return false;
    // Cannot be in final state
    if (po.fulfillment === 'satisfeita' || po.fulfillment === 'anulada') return false;
    // Cannot be in uncertain state
    if (po.sage_integration_uncertain) return false;
    return true;
  };
  
  // Sync PO from SAGE
  const handleSyncFromSage = async (poNumber) => {
    setSyncingFromSage(prev => ({ ...prev, [poNumber]: true }));
    try {
      const response = await axios.post(`${API}/purchase-orders/${poNumber}/sync-from-sage`);
      const result = response.data;
      
      if (result.has_changes) {
        // Show modal with changes
        setSyncResult(result);
        setShowSyncResultModal(true);
        toast.success(`Sincronizado: ${result.changes_count} alteração(ões) detetada(s)`);
      } else {
        toast.success('Encomenda já está atualizada com o SAGE');
      }
      
      // Refresh data
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erro ao sincronizar com SAGE';
      toast.error(detail);
    } finally {
      setSyncingFromSage(prev => ({ ...prev, [poNumber]: false }));
    }
  };
  
  // Get sync history for a PO
  const handleShowSyncHistory = async (poNumber) => {
    setLoadingSyncHistory(true);
    setShowSyncHistoryModal(true);
    try {
      const response = await axios.get(`${API}/purchase-orders/${poNumber}/sync-history`);
      setSyncHistoryData({ po_number: poNumber, ...response.data });
    } catch (error) {
      toast.error('Erro ao carregar histórico');
      setSyncHistoryData({ po_number: poNumber, history: [] });
    } finally {
      setLoadingSyncHistory(false);
    }
  };

  const toggleSupplier = (supplier) => {
    setExpandedSuppliers(prev => ({ ...prev, [supplier]: !prev[supplier] }));
  };

  const togglePO = (poNumber) => {
    setExpandedPOs(prev => ({ ...prev, [poNumber]: !prev[poNumber] }));
  };

  const getStatusBadge = (status) => {
    // Legacy status mapping — but we prefer fulfillment
    switch (status) {
      case 'received':
      case 'satisfeita':
        return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200"><CheckCircle className="h-3 w-3 mr-1" />Satisfeita</Badge>;
      case 'partially_received':
      case 'parcial':
        return <Badge className="bg-amber-100 text-amber-700 border-amber-200"><Package className="h-3 w-3 mr-1" />Parcial</Badge>;
      case 'ordered':
        return <Badge className="bg-purple-100 text-purple-700 border-purple-200"><Truck className="h-3 w-3 mr-1" />Encomendado</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-700 border-amber-200"><Clock className="h-3 w-3 mr-1" />Rascunho</Badge>;
    }
  };

  const getFulfillmentBadge = (fulfillment) => {
    switch (fulfillment) {
      case 'satisfeita':
        return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200"><CheckCircle className="h-3 w-3 mr-1" />Recebida</Badge>;
      case 'satisfeita_incompleta':
        return (
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 border-dashed" title="SAGE limpou pendentes — recebeu apenas parte do encomendado">
            <CheckCircle className="h-3 w-3 mr-1" />Recebida
            <span className="ml-1 px-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-sm">parcial</span>
          </Badge>
        );
      case 'parcial':
        return <Badge className="bg-blue-100 text-blue-700 border-blue-200"><Package className="h-3 w-3 mr-1" />Parcial</Badge>;
      case 'encomendada':
        return <Badge className="bg-purple-100 text-purple-700 border-purple-200"><Truck className="h-3 w-3 mr-1" />Encomendada</Badge>;
      case 'pronta':
        return <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200"><CheckSquare className="h-3 w-3 mr-1" />Pronta</Badge>;
      case 'nao_satisfeita':
        return <Badge className="bg-orange-100 text-orange-700 border-orange-200" title="SAGE limpou pendentes — nada foi recebido"><XCircle className="h-3 w-3 mr-1" />Não Satisfeita</Badge>;
      case 'anulada':
        return <Badge className="bg-slate-200 text-slate-500 border-slate-300 line-through"><Ban className="h-3 w-3 mr-1" />Anulada</Badge>;
      case 'pendente':
      default:
        return <Badge className="bg-amber-100 text-amber-700 border-amber-200"><Clock className="h-3 w-3 mr-1" />Rascunho</Badge>;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'received': return 'bg-emerald-100 text-emerald-700';
      case 'partially_received': return 'bg-blue-100 text-blue-700';
      case 'ordered': return 'bg-purple-100 text-purple-700';
      case 'approved': return 'bg-indigo-100 text-indigo-700';
      default: return 'bg-amber-100 text-amber-700';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'received': return 'Recebido';
      case 'partially_received': return 'Parcial';
      case 'ordered': return 'Encomendado';
      case 'approved': return 'Aprovado';
      case 'pending': return 'Rascunho';  // pending = Draft
      default: return 'Rascunho';
    }
  };

  const resetCreateForm = () => {
    setFormData({ product_id: '', quantity: 0, supplier: '' });
    setSelectedProduct(null);
    setSearchQuery('');
  };

  if (loading) {
    return <LoadingState message="A carregar encomendas..." />;
  }

  return (
    <div className="p-4 sm:p-6 max-w-[1400px] mx-auto" data-testid="purchase-orders-page">
      {/* Header */}
      <PageHeader
        title="Encomendas"
        subtitle={`${orders.length} encomendas · ${Object.keys(groupedOrders).length} fornecedores`}
        actions={
          <div className="flex gap-2 flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSyncSageStatus}
              disabled={syncingSage}
              className="text-slate-600 hover:text-slate-900"
              data-testid="sync-sage-status"
            >
              {syncingSage ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              <span className="hidden sm:inline ml-2">{syncingSage ? 'A sincronizar...' : 'Atualizar'}</span>
            </Button>
            <Dialog open={showCreateModal} onOpenChange={(open) => { setShowCreateModal(open); if (!open) resetCreateForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="create-po-button" size="sm" className="bg-slate-800 hover:bg-slate-700">
                <Plus className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">Nova Encomenda</span>
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-[550px] bg-white max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Criar Encomenda</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Procurar Produto por Código/Nome *</Label>
                <Popover open={searchOpen} onOpenChange={setSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={searchOpen}
                      className="w-full justify-between h-auto min-h-[40px] px-3"
                    >
                      {selectedProduct ? (
                        <div className="flex flex-col items-start">
                          <span className="font-mono text-sm text-red-600">{selectedProduct.sku}</span>
                          <span className="text-sm">{selectedProduct.name}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">Digite o código ou nome...</span>
                      )}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[500px] p-0 bg-white" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder="Pesquisar por SKU ou nome..."
                        value={searchQuery}
                        onValueChange={setSearchQuery}
                      />
                      <CommandList>
                        <CommandEmpty>Nenhum produto encontrado.</CommandEmpty>
                        <CommandGroup>
                          {filteredProducts.map((product) => (
                            <CommandItem
                              key={product.id}
                              value={product.id}
                              onSelect={() => handleProductSelect(product)}
                              className="cursor-pointer"
                            >
                              <div className="flex items-center justify-between w-full">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono text-sm font-semibold text-red-600">{product.sku}</span>
                                    <Check className={cn("h-4 w-4", selectedProduct?.id === product.id ? "opacity-100" : "opacity-0")} />
                                  </div>
                                  <p className="text-sm">{product.name}</p>
                                </div>
                                <div className="text-right ml-4">
                                  <p className="font-mono text-sm font-semibold">€{(product.unit_price ?? 0).toFixed(2)}</p>
                                  <p className="text-xs text-muted-foreground">{product.supplier || 'N/A'}</p>
                                </div>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {selectedProduct && (
                <Card className="border border-emerald-200 bg-emerald-50/30 rounded-sm">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div><p className="text-muted-foreground">SKU</p><p className="font-mono font-semibold">{selectedProduct.sku}</p></div>
                      <div><p className="text-muted-foreground">Preço</p><p className="font-mono font-semibold">€{(selectedProduct.unit_price ?? 0).toFixed(2)}</p></div>
                      <div><p className="text-muted-foreground">Stock</p><p className="font-mono">{selectedProduct.current_stock}</p></div>
                      <div><p className="text-muted-foreground">Fornecedor</p><p>{selectedProduct.supplier || 'N/A'}</p></div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="space-y-2">
                <Label htmlFor="quantity">Quantidade *</Label>
                <Input id="quantity" type="number" min="1" value={formData.quantity} onChange={(e) => setFormData({ ...formData, quantity: e.target.value })} required />
              </div>

              <div className="space-y-2">
                <Label htmlFor="supplier">Fornecedor</Label>
                <Input id="supplier" value={formData.supplier} onChange={(e) => setFormData({ ...formData, supplier: e.target.value })} placeholder={selectedProduct?.supplier || "Fornecedor"} />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1 bg-red-600 hover:bg-red-700" disabled={!selectedProduct || formData.quantity <= 0}>Criar Encomenda</Button>
                <Button type="button" variant="outline" onClick={() => { setShowCreateModal(false); resetCreateForm(); }} className="flex-1">Cancelar</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
          </div>
        }
      />

      {/* Search & KPIs Strip */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4 pb-4 border-b border-slate-100">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Pesquisar PO, fornecedor, ENF ou SKU..."
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
              className="pl-9 h-9 bg-slate-50 border-slate-200 focus:bg-white"
              data-testid="global-search-input"
            />
            {globalSearch && (
              <button
                onClick={() => setGlobalSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <XCircle className="h-4 w-4" />
              </button>
            )}
          </div>
          
          {/* KPIs - Clickable Filters */}
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              onClick={() => setStatusFilter('pendente')}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-all ${
                statusFilter === 'pendente' 
                  ? 'bg-amber-100 text-amber-800 ring-1 ring-amber-300' 
                  : 'bg-slate-50 text-slate-600 hover:bg-amber-50 hover:text-amber-700'
              }`}
              data-testid="filter-rascunhos"
            >
              <span className="font-mono font-bold">{stats.rascunhos}</span> Rascunhos
            </button>
            <button
              onClick={() => setStatusFilter('pronta')}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-all ${
                statusFilter === 'pronta' 
                  ? 'bg-indigo-100 text-indigo-800 ring-1 ring-indigo-300' 
                  : 'bg-slate-50 text-slate-600 hover:bg-indigo-50 hover:text-indigo-700'
              }`}
              data-testid="filter-prontas"
            >
              <span className="font-mono font-bold">{stats.prontas}</span> Prontas
            </button>
            <button
              onClick={() => setStatusFilter('a_receber')}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-all ${
                statusFilter === 'a_receber' 
                  ? 'bg-purple-100 text-purple-800 ring-1 ring-purple-300' 
                  : 'bg-slate-50 text-slate-600 hover:bg-purple-50 hover:text-purple-700'
              }`}
              data-testid="filter-a-receber"
            >
              <span className="font-mono font-bold">{stats.encomendadas + stats.parciais}</span> A Receber
            </button>
            <button
              onClick={() => setStatusFilter('satisfeita')}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-all ${
                statusFilter === 'satisfeita' 
                  ? 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300' 
                  : 'bg-slate-50 text-slate-600 hover:bg-emerald-50 hover:text-emerald-700'
              }`}
              data-testid="filter-recebidas"
            >
              <span className="font-mono font-bold">{stats.recebidas}</span> Recebidas
            </button>
            <button
              onClick={() => setStatusFilter('all')}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-all ${
                statusFilter === 'all' 
                  ? 'bg-slate-200 text-slate-800 ring-1 ring-slate-300' 
                  : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
              }`}
              data-testid="filter-all"
            >
              Todas
            </button>
            {statusFilter !== 'active' && (
              <button
                onClick={() => setStatusFilter('active')}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-400 hover:text-slate-600"
              >
                <XCircle className="h-3.5 w-3.5" /> Limpar
              </button>
            )}
            <span className="hidden lg:inline-flex items-center gap-1 ml-2 px-2 py-1 bg-slate-50 rounded text-xs text-slate-500">
              €{stats.aReceberValue.toLocaleString('pt-PT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} a receber
            </span>
          </div>
        </div>

      {/* Grouped Orders */}
      {orders.length === 0 ? (
        <Card className="border border-slate-200 rounded-sm shadow-none">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <FileText className="h-16 w-16 text-slate-300 mb-4" />
            <p className="text-lg font-medium text-slate-900 mb-2">Ainda sem encomendas</p>
            <p className="text-sm text-muted-foreground mb-6">Crie a sua primeira encomenda para começar</p>
            <Button onClick={() => setShowCreateModal(true)} className="bg-slate-900 hover:bg-slate-800">
              <Plus className="h-4 w-4 mr-2" />Nova Encomenda
            </Button>
          </CardContent>
        </Card>
      ) : groupedOrders.length === 0 ? (
        <Card className="border border-slate-200 rounded-sm shadow-none">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <CheckCircle className="h-16 w-16 text-emerald-300 mb-4" />
            <p className="text-lg font-medium text-slate-900 mb-2">
              {statusFilter === 'active' ? 'Sem encomendas ativas' : 'Nenhuma encomenda com este filtro'}
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              {statusFilter === 'active' ? 'Todas as encomendas foram recebidas' : ''}
            </p>
            <div className="flex gap-2">
              {statusFilter !== 'active' && (
                <Button variant="outline" onClick={() => setStatusFilter('active')}>Ver Ativas</Button>
              )}
              <Button variant="outline" onClick={() => setStatusFilter('all')}>Ver Todas ({stats.totalPOs})</Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {groupedOrders.map((supplierGroup) => (
            <div key={supplierGroup.name} className="border border-slate-200 rounded-lg overflow-hidden">
              {/* Supplier Header - Ultra Compact */}
              <Collapsible open={!!expandedSuppliers[supplierGroup.name] || !!globalSearch} onOpenChange={() => toggleSupplier(supplierGroup.name)}>
                <CollapsibleTrigger asChild>
                  <div className="flex items-center justify-between px-3 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors bg-white">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      {(expandedSuppliers[supplierGroup.name] || globalSearch) ? (
                        <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm text-slate-900 truncate">{supplierGroup.name}</span>
                          {supplierGroup.sageSupplierCodes && supplierGroup.sageSupplierCodes.size === 1 && (
                            <span className="text-[11px] font-mono text-slate-500" data-testid={`supplier-sage-code-${supplierGroup.name}`}>
                              {[...supplierGroup.sageSupplierCodes][0]}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
                          <span>{supplierGroup.orders.length} encomenda{supplierGroup.orders.length !== 1 ? 's' : ''}</span>
                          {(() => {
                            const counts = supplierGroup.orders.reduce((acc, po) => {
                              acc[po.fulfillment] = (acc[po.fulfillment] || 0) + 1;
                              return acc;
                            }, {});
                            return (
                              <>
                                {counts.pendente > 0 && <span className="text-amber-600">· {counts.pendente} rascunho{counts.pendente !== 1 ? 's' : ''}</span>}
                                {(counts.encomendada > 0 || counts.parcial > 0) && <span className="text-purple-600">· {(counts.encomendada || 0) + (counts.parcial || 0)} a receber</span>}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <span className={`font-mono text-sm font-semibold ${supplierGroup.pendingValue > 0 ? 'text-slate-900' : 'text-slate-400'}`}>
                        €{supplierGroup.pendingValue.toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </CollapsibleTrigger>

                <CollapsibleContent>
                  <div className="border-t border-slate-100 bg-slate-50/30 px-3 py-2 space-y-1.5">
                    {/* POs within this supplier - Clean Cards */}
                    {supplierGroup.orders.map((po) => (
                      <Collapsible key={po.po_number} open={!!expandedPOs[po.po_number]} onOpenChange={() => togglePO(po.po_number)}>
                        {/* PO Row - Compact */}
                        <div data-po-anchor={po.po_number} className="bg-white rounded border border-slate-200 overflow-hidden">
                          <CollapsibleTrigger asChild>
                            <div className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-slate-50 transition-colors">
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                {expandedPOs[po.po_number] ? (
                                  <ChevronDown className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                                ) : (
                                  <ChevronRight className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                                )}
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-mono text-sm font-medium text-slate-900">{po.po_number}</span>
                                    {getFulfillmentBadge(po.fulfillment)}
                                    {/* ENF reference - only show once, cleanly */}
                                    {po.sage_po_number && (
                                      <span className="text-[11px] font-mono text-slate-500">{po.sage_po_number}</span>
                                    )}
                                    {/* Communication Status Indicator */}
                                    {emailSentStatus[po.po_number]?.status === 'new_reply' && (
                                      <span 
                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-amber-50 text-amber-700 border border-amber-300 cursor-pointer hover:bg-amber-100 animate-pulse"
                                        onClick={(e) => { e.stopPropagation(); handleShowEmailHistory(po.po_number); }}
                                        title="Nova resposta do fornecedor"
                                      >
                                        <MailCheck className="h-3 w-3" />
                                        Nova resposta
                                      </span>
                                    )}
                                    {emailSentStatus[po.po_number]?.status === 'reply_read' && (
                                      <span 
                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-emerald-50 text-emerald-600 border border-emerald-200 cursor-pointer hover:bg-emerald-100"
                                        onClick={(e) => { e.stopPropagation(); handleShowEmailHistory(po.po_number); }}
                                        title="Resposta recebida e lida"
                                      >
                                        <MailCheck className="h-3 w-3" />
                                        Resposta
                                        {emailSentStatus[po.po_number]?.reply_count > 1 && (
                                          <span className="font-medium">({emailSentStatus[po.po_number].reply_count})</span>
                                        )}
                                      </span>
                                    )}
                                    {emailSentStatus[po.po_number]?.status === 'sent' && (
                                      <span 
                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-blue-50 text-blue-600 border border-blue-200 cursor-pointer hover:bg-blue-100"
                                        onClick={(e) => { e.stopPropagation(); handleShowEmailHistory(po.po_number); }}
                                        title={`Email enviado (${emailSentStatus[po.po_number]?.sent_count || 1}x)`}
                                      >
                                        <MailCheck className="h-3 w-3" />
                                        Enviado
                                        {emailSentStatus[po.po_number]?.sent_count > 1 && (
                                          <span className="font-medium">({emailSentStatus[po.po_number].sent_count}x)</span>
                                        )}
                                      </span>
                                    )}
                                    {emailSentStatus[po.po_number]?.status === 'delivered' && (
                                      <span 
                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-emerald-50 text-emerald-600 border border-emerald-200 cursor-pointer hover:bg-emerald-100"
                                        onClick={(e) => { e.stopPropagation(); handleShowEmailHistory(po.po_number); }}
                                        title={`Email entregue (${emailSentStatus[po.po_number]?.sent_count || 1}x)`}
                                      >
                                        <MailCheck className="h-3 w-3" />
                                        Entregue
                                        {emailSentStatus[po.po_number]?.sent_count > 1 && (
                                          <span className="font-medium">({emailSentStatus[po.po_number].sent_count}x)</span>
                                        )}
                                      </span>
                                    )}
                                    {(emailSentStatus[po.po_number]?.status === 'bounced' || emailSentStatus[po.po_number]?.status === 'failed') && (
                                      <span 
                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-red-50 text-red-600 border border-red-200 cursor-pointer hover:bg-red-100"
                                        onClick={(e) => { e.stopPropagation(); handleShowEmailHistory(po.po_number); }}
                                        title="Email falhou ou foi rejeitado"
                                      >
                                        <MailCheck className="h-3 w-3" />
                                        Falhou
                                      </span>
                                    )}
                                    {/* SAGE Integration Error Badge */}
                                    {po.sage_integration_error && (
                                      <span 
                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-red-50 text-red-700 border border-red-300 cursor-pointer hover:bg-red-100"
                                        onClick={(e) => { e.stopPropagation(); toast.error(po.sage_integration_error, { duration: 8000 }); }}
                                        title={`Erro SAGE: ${po.sage_integration_error}`}
                                      >
                                        <AlertTriangle className="h-3 w-3" />
                                        Erro SAGE
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2 mt-0.5 text-[11px] text-slate-500">
                                    <span>{po.items.length} artigo{po.items.length !== 1 ? 's' : ''}</span>
                                    <span>·</span>
                                    <span className={po.fulfillment === 'satisfeita' ? 'text-emerald-600 font-medium' : ''}>
                                      {po.receivedQuantity}/{po.totalQuantity} un.
                                    </span>
                                    {/* POs canceladas não mostram "X pendentes" — o badge principal "Anulada" comunica o estado terminal */}
                                    {po.totalQuantity > 0 && po.receivedQuantity < po.totalQuantity && 
                                      po.status !== 'cancelled' && po.fulfillment !== 'anulada' && (
                                      <>
                                        <span>·</span>
                                        <span className="text-purple-600">{po.totalQuantity - po.receivedQuantity} pendentes</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <span className="font-mono text-sm text-slate-700">€{po.totalValue.toFixed(2)}</span>
                                {/* Primary Action Button */}
                                {canEditPO(po) ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-xs bg-amber-50 text-amber-700 hover:bg-amber-100"
                                    onClick={(e) => { e.stopPropagation(); handleContinueDraft(po); }}
                                    data-testid={`po-continue-${po.po_number}`}
                                  >
                                    Continuar
                                  </Button>
                                ) : po.fulfillment === 'pronta' ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-xs bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                                    onClick={(e) => { e.stopPropagation(); handleSendPOToSage(po.po_number); }}
                                    disabled={sendingSagePO[po.po_number]}
                                    data-testid={`po-send-sage-${po.po_number}`}
                                  >
                                    {sendingSagePO[po.po_number] ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Enviar SAGE'}
                                  </Button>
                                ) : (po.fulfillment === 'encomendada' || po.fulfillment === 'parcial') ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-xs bg-slate-50 text-slate-600 hover:bg-slate-100"
                                    onClick={(e) => { e.stopPropagation(); togglePO(po.po_number); }}
                                  >
                                    Abrir
                                  </Button>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0 text-slate-400 hover:text-slate-600"
                                    onClick={(e) => { e.stopPropagation(); togglePO(po.po_number); }}
                                  >
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </CollapsibleTrigger>

                          {/* PO Items - Clean Detail View */}
                          <CollapsibleContent>
                            <div className="border-t border-slate-100 bg-slate-50/50">
                              {/* SAGE Document References - Compact */}
                              {po.sage_po_number && (
                                <div className="px-3 py-1.5 bg-slate-100/50 border-b border-slate-100 flex items-center gap-3 text-[11px]">
                                  <span className="text-slate-500">ENF:</span>
                                  <span className="font-mono font-medium text-slate-700">{po.sage_po_number}</span>
                                  {po.entered_sage_at && (
                                    <span className="text-slate-400">{new Date(po.entered_sage_at).toLocaleDateString('pt-PT')}</span>
                                  )}
                                  {po.sage_satisfaction_docs && po.sage_satisfaction_docs.length > 0 && (
                                    <>
                                      <span className="text-slate-400">|</span>
                                      <span className="text-slate-500">Satisfeita:</span>
                                      {po.sage_satisfaction_docs.map((d, i) => (
                                        <span key={d.reference || i} className="font-mono text-emerald-600">{d.reference}</span>
                                      ))}
                                    </>
                                  )}
                                </div>
                              )}
                              
                              {/* Action Bar for this PO */}
                              <div className="px-3 py-1.5 border-b border-slate-100 flex items-center gap-2 justify-end">
                                {/* PDF Preview & Download - only for integrated POs */}
                                {isPOIntegrated(po) && (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 px-2 text-[11px] text-slate-600 hover:text-slate-700 hover:bg-slate-100"
                                      onClick={(e) => { e.stopPropagation(); handlePreviewPDF(po.po_number); }}
                                      disabled={loadingPDFPreview && pdfPreviewPO === po.po_number}
                                      data-testid={`po-preview-${po.po_number}`}
                                      title="Visualizar PDF"
                                    >
                                      {loadingPDFPreview && pdfPreviewPO === po.po_number ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Eye className="h-3 w-3 mr-1" />Ver</>}
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 px-2 text-[11px] text-red-600 hover:text-red-700 hover:bg-red-50"
                                      onClick={(e) => { e.stopPropagation(); handleDownloadPDF(po.po_number); }}
                                      disabled={generatingPDF === po.po_number}
                                      data-testid={`po-pdf-${po.po_number}`}
                                      title="Descarregar PDF"
                                    >
                                      {generatingPDF === po.po_number ? <Loader2 className="h-3 w-3 animate-spin" /> : <><FileDown className="h-3 w-3 mr-1" />PDF</>}
                                    </Button>
                                  </>
                                )}
                                {/* Email - only for integrated POs */}
                                {isPOIntegrated(po) && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className={`h-6 px-2 text-[11px] ${emailSentStatus[po.po_number]?.sent ? 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50' : 'text-blue-600 hover:text-blue-700 hover:bg-blue-50'}`}
                                    onClick={(e) => { e.stopPropagation(); handleOpenEmailModal(po); }}
                                    data-testid={`po-email-${po.po_number}`}
                                    title={emailSentStatus[po.po_number]?.sent ? 'Reenviar encomenda por email' : 'Enviar encomenda por email'}
                                  >
                                    {emailSentStatus[po.po_number]?.sent ? (
                                      <><MailCheck className="h-3 w-3 mr-1" />Reenviar</>
                                    ) : (
                                      <><Mail className="h-3 w-3 mr-1" />Email</>
                                    )}
                                  </Button>
                                )}
                                {/* Export Excel */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 px-2 text-[11px] text-slate-500 hover:text-slate-700"
                                  onClick={(e) => { e.stopPropagation(); handleExportExcel(po.po_number, supplierGroup.name); }}
                                >
                                  <FileSpreadsheet className="h-3 w-3 mr-1" />Excel
                                </Button>
                                {/* Send to SAGE if not sent */}
                                {!(po.sage_synced || po.entered_sage_at) && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 text-[11px] text-blue-600 hover:text-blue-700"
                                    onClick={(e) => { e.stopPropagation(); handleSendPOToSage(po.po_number); }}
                                    disabled={sendingSagePO[po.po_number]}
                                  >
                                    {sendingSagePO[po.po_number] ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Upload className="h-3 w-3 mr-1" />SAGE</>}
                                  </Button>
                                )}
                                {/* Probe satisfaction docs */}
                                {po.sage_synced && (po.fulfillment === 'satisfeita' || po.fulfillment === 'parcial' || po.fulfillment === 'nao_satisfeita' || po.fulfillment === 'encomendada') && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 text-[11px] text-slate-500 hover:text-slate-700"
                                    onClick={(e) => { e.stopPropagation(); handleProbeSatisfactionDocs(po.po_number); }}
                                    disabled={probingPO === po.po_number}
                                    data-testid={`probe-docs-btn-${po.po_number}`}
                                  >
                                    {probingPO === po.po_number ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Search className="h-3 w-3 mr-1" />GEF/CCF</>}
                                  </Button>
                                )}
                                {/* SAGE Sync - only for eligible POs (integrated, no receptions) */}
                                {canSyncFromSage(po) && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 text-[11px] text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50"
                                    onClick={(e) => { e.stopPropagation(); handleSyncFromSage(po.po_number); }}
                                    disabled={syncingFromSage[po.po_number]}
                                    data-testid={`sync-sage-btn-${po.po_number}`}
                                    title="Sincronizar alterações do SAGE"
                                  >
                                    {syncingFromSage[po.po_number] ? <Loader2 className="h-3 w-3 animate-spin" /> : <><RefreshCw className="h-3 w-3 mr-1" />Sync SAGE</>}
                                  </Button>
                                )}
                                {/* Show sync history if has synced */}
                                {po.sage_last_content_sync && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 text-[11px] text-slate-400 hover:text-slate-600"
                                    onClick={(e) => { e.stopPropagation(); handleShowSyncHistory(po.po_number); }}
                                    title={`Última sync: ${new Date(po.sage_last_content_sync).toLocaleString('pt-PT')}`}
                                  >
                                    <History className="h-3 w-3" />
                                  </Button>
                                )}
                                {/* Cancel button - HIDDEN for SAGE-integrated POs to maintain consistency */}
                                {po.fulfillment !== 'anulada' && po.fulfillment !== 'satisfeita' &&
                                  !isPOIntegrated(po) && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 text-[11px] text-slate-400 hover:text-red-600"
                                    onClick={(e) => { e.stopPropagation(); handleCancelPO(po.po_number, false); }}
                                    disabled={cancellingPO === po.po_number}
                                    data-testid={`po-cancel-${po.po_number}`}
                                  >
                                    {cancellingPO === po.po_number ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Ban className="h-3 w-3 mr-1" />Anular</>}
                                  </Button>
                                )}
                              </div>
                              
                              {/* Items Table - Clean */}
                              <div className="divide-y divide-slate-100">
                                {po.items.map((item, idx) => {
                                  const receivedQty = item.received_quantity || 0;
                                  const isFullyReceived = item.status === 'received';
                                  const isPoSageClosed = po.fulfillment === 'satisfeita_incompleta' || po.fulfillment === 'nao_satisfeita';
                                  const isLineUnsatisfied = isPoSageClosed && !isFullyReceived;
                                  const unsatisfiedQty = Math.max(0, (item.quantity || 0) - receivedQty);

                                  return (
                                    <div 
                                      key={item.id} 
                                      className={`flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 ${idx !== po.items.length - 1 ? 'border-b border-slate-100' : ''} ${isLineUnsatisfied ? 'bg-slate-50/60' : (!isFullyReceived ? 'bg-amber-50/30' : '')}`}
                                      data-testid={`po-line-${item.id}`}
                                    >
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className="font-mono text-xs text-red-600">{item.sku}</span>
                                          <span className="text-sm truncate">{item.product_name}</span>
                                        </div>
                                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                                          {/* Quantity - editable if draft */}
                                          {editingQuantity?.itemId === item.id ? (
                                            <span className="flex items-center gap-1">
                                              <Input
                                                type="number"
                                                min="1"
                                                className="w-16 h-6 text-xs font-mono"
                                                defaultValue={editingQuantity.currentQty}
                                                autoFocus
                                                onKeyDown={(e) => {
                                                  if (e.key === 'Enter') {
                                                    handleInlineQuantityEdit(item.id, parseInt(e.target.value));
                                                  } else if (e.key === 'Escape') {
                                                    setEditingQuantity(null);
                                                  }
                                                }}
                                                onBlur={(e) => {
                                                  const val = parseInt(e.target.value);
                                                  if (val !== editingQuantity.currentQty && val > 0) {
                                                    handleInlineQuantityEdit(item.id, val);
                                                  } else {
                                                    setEditingQuantity(null);
                                                  }
                                                }}
                                                disabled={savingEdit}
                                              />
                                              <span className="text-muted-foreground">un.</span>
                                            </span>
                                          ) : (
                                            <span 
                                              className={`font-mono ${canEditPO(po) ? 'cursor-pointer hover:text-amber-600 hover:underline' : ''}`}
                                              onClick={(e) => {
                                                if (canEditPO(po)) {
                                                  e.stopPropagation();
                                                  setEditingQuantity({ itemId: item.id, currentQty: item.quantity });
                                                }
                                              }}
                                              title={canEditPO(po) ? 'Clique para editar quantidade' : undefined}
                                            >
                                              {item.quantity} un.
                                              {canEditPO(po) && <Pencil className="inline h-3 w-3 ml-1 opacity-40" />}
                                            </span>
                                          )}
                                          {isLineUnsatisfied ? (
                                            receivedQty === 0 ? (
                                              <span
                                                className="font-mono text-orange-700"
                                                data-testid={`po-line-unsatisfied-total-${item.id}`}
                                                title="O SAGE fechou esta encomenda — as unidades pendentes não vão chegar"
                                              >
                                                Totalmente não satisfeito · {item.quantity} un. não chegaram
                                              </span>
                                            ) : (
                                              <span
                                                className="font-mono"
                                                data-testid={`po-line-unsatisfied-partial-${item.id}`}
                                                title="O SAGE fechou esta encomenda — as unidades em falta não vão chegar"
                                              >
                                                <span className="text-emerald-600">{receivedQty}/{item.quantity} recebidos</span>
                                                <span className="text-orange-700"> · {unsatisfiedQty} não satisfeitos</span>
                                              </span>
                                            )
                                          ) : (
                                            <span className={`font-mono ${receivedQty > 0 ? 'text-emerald-600' : ''}`}>
                                              {receivedQty}/{item.quantity} recebidos
                                            </span>
                                          )}
                                          {/* Price - editable if draft */}
                                          {editingPrice?.itemId === item.id ? (
                                            <span className="flex items-center gap-1">
                                              <span>€</span>
                                              <Input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                className="w-20 h-6 text-xs font-mono"
                                                defaultValue={(item.unit_price || 0).toFixed(2)}
                                                autoFocus
                                                onKeyDown={(e) => {
                                                  if (e.key === 'Enter') {
                                                    handleInlinePriceEdit(item.id, parseFloat(e.target.value));
                                                  } else if (e.key === 'Escape') {
                                                    setEditingPrice(null);
                                                  }
                                                }}
                                                onBlur={(e) => {
                                                  const val = parseFloat(e.target.value);
                                                  if (val !== editingPrice.currentPrice) {
                                                    handleInlinePriceEdit(item.id, val);
                                                  } else {
                                                    setEditingPrice(null);
                                                  }
                                                }}
                                                disabled={savingEdit}
                                              />
                                            </span>
                                          ) : (
                                            <span 
                                              className={`font-mono font-semibold ${canEditPO(po) ? 'cursor-pointer hover:text-amber-600 hover:underline' : ''} ${(item.unit_price || 0) === 0 ? 'text-red-500' : ''}`}
                                              onClick={(e) => {
                                                if (canEditPO(po)) {
                                                  e.stopPropagation();
                                                  setEditingPrice({ itemId: item.id, currentPrice: item.unit_price || 0 });
                                                }
                                              }}
                                              title={canEditPO(po) ? 'Clique para editar preço' : ((item.unit_price || 0) === 0 ? 'Preço por definir' : undefined)}
                                            >
                                              {(item.unit_price || 0) === 0 ? 'N/D' : `€${(item.unit_price || 0).toFixed(2)}`}
                                              {canEditPO(po) && <Pencil className="inline h-3 w-3 ml-1 opacity-40" />}
                                            </span>
                                          )}
                                          <span className="font-mono text-slate-500">
                                            Total: €{(item.total_amount || 0).toFixed(2)}
                                          </span>
                                        </div>
                                      </div>
                                      {/* Right side - status indicator only (no actions) */}
                                      <div className="flex items-center gap-2 flex-shrink-0">
                                        {isLineUnsatisfied ? (
                                          <Badge
                                            className="bg-orange-100 text-orange-700 border-orange-200 h-6 px-2 text-[11px] font-normal"
                                            title="SAGE limpou esta linha — não vai haver mais entregas"
                                            data-testid={`po-line-badge-unsatisfied-${item.id}`}
                                          >
                                            <XCircle className="h-3 w-3 mr-1" />
                                            Não satisfeito
                                          </Badge>
                                        ) : isFullyReceived ? (
                                          <Badge className="bg-emerald-50 text-emerald-600 border-emerald-200 h-6 px-2 text-[11px] font-normal">
                                            <CheckCircle className="h-3 w-3 mr-1" />
                                            Recebido
                                          </Badge>
                                        ) : receivedQty > 0 ? (
                                          <Badge className="bg-blue-50 text-blue-600 border-blue-200 h-6 px-2 text-[11px] font-normal">
                                            <Package className="h-3 w-3 mr-1" />
                                            Parcial
                                          </Badge>
                                        ) : (po.sage_synced || po.entered_sage_at) && po.status !== 'cancelled' && po.fulfillment !== 'anulada' ? (
                                          <Badge className="bg-purple-50 text-purple-600 border-purple-200 h-6 px-2 text-[11px] font-normal">
                                            <Clock className="h-3 w-3 mr-1" />
                                            A aguardar
                                          </Badge>
                                        ) : null}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </CollapsibleContent>
                          </div>
                        </Collapsible>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            ))}
          </div>
        )}

      {/* SAGE PO Number Modal */}
      <Dialog open={showSageModal} onOpenChange={setShowSageModal}>
        <DialogContent className="sm:max-w-[450px] bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckSquare className="h-5 w-5 text-green-600" />
              Registar no SAGE 100C
            </DialogTitle>
            <DialogDescription>
              Encomenda {selectedPOForSage}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="sage-po-number">Número da Encomenda no SAGE *</Label>
              <Input
                id="sage-po-number"
                value={sagePoNumber}
                onChange={(e) => setSagePoNumber(e.target.value)}
                placeholder="Ex: ECF-2024/00123"
                className="font-mono"
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                Introduza o número atribuído pelo SAGE 100C após importar o ficheiro Excel
              </p>
            </div>

            <div className="p-3 rounded-sm border border-green-200 bg-green-50/50">
              <div className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-900">Ao confirmar:</p>
                  <ul className="text-xs text-green-700 mt-1 space-y-1">
                    <li>• A encomenda será marcada como &quot;Encomendado&quot;</li>
                    <li>• O número SAGE ficará associado para rastreio</li>
                    <li>• Poderá desmarcar se necessário</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowSageModal(false)}>
              Cancelar
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={handleConfirmSageEntered}
              disabled={submittingSage || !sagePoNumber.trim()}
            >
              {submittingSage ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  A processar...
                </>
              ) : 'Confirmar Registo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="sm:max-w-[550px] bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-600" />
              Enviar Encomenda por Email
            </DialogTitle>
            <DialogDescription>
              {emailModalPO?.sage_po_number || emailModalPO?.po_number}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="email-to">Para *</Label>
                {emailForm.to && emailForm.to.includes('@') && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-[10px] text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    onClick={handleSaveSupplierEmail}
                    disabled={savingSupplierEmail}
                    title="Guardar este email como contacto permanente do fornecedor"
                  >
                    {savingSupplierEmail ? (
                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                    ) : (
                      <Save className="h-3 w-3 mr-1" />
                    )}
                    Guardar como contacto
                  </Button>
                )}
              </div>
              <Input
                id="email-to"
                type="email"
                value={emailForm.to}
                onChange={(e) => setEmailForm(prev => ({ ...prev, to: e.target.value }))}
                placeholder="email@fornecedor.pt"
              />
              {!emailForm.to && (
                <p className="text-xs text-amber-600">Fornecedor sem email configurado. Introduza manualmente.</p>
              )}
              {emailForm.to && emailForm.emailSource === 'stockflow' && (
                <p className="text-xs text-emerald-600 flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  Email gerido pela StockFlow (não será alterado pelo SAGE)
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email-cc">CC (opcional)</Label>
              <Input
                id="email-cc"
                type="email"
                value={emailForm.cc}
                onChange={(e) => setEmailForm(prev => ({ ...prev, cc: e.target.value }))}
                placeholder="copia@empresa.pt"
                className={emailForm.cc && emailForm.to && emailForm.cc.trim().toLowerCase() === emailForm.to.trim().toLowerCase() ? 'border-amber-400' : ''}
              />
              {emailForm.cc && emailForm.to && emailForm.cc.trim().toLowerCase() === emailForm.to.trim().toLowerCase() && (
                <p className="text-xs text-amber-600">
                  CC igual a Para — será ignorado (não enviado em duplicado)
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email-subject">Assunto</Label>
              <Input
                id="email-subject"
                value={emailForm.subject}
                onChange={(e) => setEmailForm(prev => ({ ...prev, subject: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email-message">Mensagem</Label>
              <Textarea
                id="email-message"
                value={emailForm.message}
                onChange={(e) => setEmailForm(prev => ({ ...prev, message: e.target.value }))}
                rows={6}
                className="resize-none"
              />
            </div>

            <div className="p-3 rounded-sm border border-slate-200 bg-slate-50">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <FileDown className="h-4 w-4" />
                <span>Anexo: <span className="font-mono text-xs">CHAVIARTE_{emailModalPO?.sage_po_number?.replace(/\//g, '-')}.pdf</span></span>
              </div>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowEmailModal(false)}>
              Cancelar
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleSendEmail}
              disabled={sendingEmail || !emailForm.to.includes('@')}
            >
              {sendingEmail ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />A enviar...</>
              ) : (
                <><Send className="h-4 w-4 mr-2" />Enviar Encomenda</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Communications Modal */}
      <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
        <DialogContent className="sm:max-w-[600px] bg-white max-h-[80vh] overflow-hidden flex flex-col" aria-describedby="communication-history-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-slate-600" />
              Comunicações por Email
              {communicationData?.enf_reference && (
                <span className="text-sm font-normal text-slate-500">— {communicationData.enf_reference}</span>
              )}
            </DialogTitle>
            <p id="communication-history-description" className="sr-only">
              Histórico de emails enviados e respostas recebidas para esta encomenda
            </p>
          </DialogHeader>
          
          {loadingCommunication ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="space-y-4 mt-4 overflow-y-auto flex-1 pr-2">
              {/* Outbound Section */}
              <div>
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-2">
                  <Send className="h-3.5 w-3.5" />
                  Enviados ({communicationData?.sent_count || 0})
                </h4>
                {(!communicationData?.outbound || communicationData.outbound.length === 0) ? (
                  <p className="text-sm text-slate-400 italic py-2">Nenhum email enviado</p>
                ) : (
                  <div className="space-y-2">
                    {communicationData.outbound.map((entry, idx) => {
                      // Map delivery_status to UI state
                      const getDeliveryState = () => {
                        const status = entry.delivery_status || entry.status;
                        switch(status) {
                          case 'delivered': return { icon: 'check-double', color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Entregue', tooltip: 'Entregue ao servidor de email do destinatário. A colocação na caixa de entrada depende dos filtros do destinatário.' };
                          case 'sent': return { icon: 'check', color: 'text-blue-500', bg: 'bg-blue-50', label: 'Enviado', tooltip: 'Email enviado, aguarda confirmação de entrega.' };
                          case 'delayed': return { icon: 'clock', color: 'text-amber-500', bg: 'bg-amber-50', label: 'Atrasado', tooltip: 'Entrega temporariamente atrasada. O sistema tentará novamente.' };
                          case 'bounced': return { icon: 'x', color: 'text-red-600', bg: 'bg-red-50', label: 'Rejeitado', tooltip: entry.bounce_reason || 'Email rejeitado pelo servidor do destinatário.' };
                          case 'complained': return { icon: 'alert', color: 'text-orange-600', bg: 'bg-orange-50', label: 'Spam', tooltip: 'Destinatário marcou como spam.' };
                          case 'suppressed': return { icon: 'ban', color: 'text-red-600', bg: 'bg-red-50', label: 'Suprimido', tooltip: 'Email não enviado - destinatário em lista de supressão.' };
                          case 'failed': return { icon: 'x', color: 'text-red-600', bg: 'bg-red-50', label: 'Falhou', tooltip: entry.error || 'Falha no envio.' };
                          case 'queuing': return { icon: 'loader', color: 'text-slate-400', bg: 'bg-slate-50', label: 'A enviar', tooltip: 'Email em fila de envio.' };
                          default: return { icon: 'check', color: 'text-blue-500', bg: 'bg-blue-50', label: 'Enviado', tooltip: '' };
                        }
                      };
                      const state = getDeliveryState();
                      
                      return (
                        <div key={idx} className={`p-3 rounded border border-slate-200 ${state.bg}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                {state.icon === 'check-double' ? (
                                  <div className={`flex items-center ${state.color}`} title={state.tooltip}>
                                    <CheckCircle className="h-4 w-4 flex-shrink-0" />
                                    <CheckCircle className="h-3 w-3 flex-shrink-0 -ml-2" />
                                  </div>
                                ) : state.icon === 'check' ? (
                                  <CheckCircle className={`h-4 w-4 flex-shrink-0 ${state.color}`} title={state.tooltip} />
                                ) : state.icon === 'clock' ? (
                                  <Clock className={`h-4 w-4 flex-shrink-0 ${state.color}`} title={state.tooltip} />
                                ) : state.icon === 'alert' ? (
                                  <AlertTriangle className={`h-4 w-4 flex-shrink-0 ${state.color}`} title={state.tooltip} />
                                ) : state.icon === 'ban' ? (
                                  <XCircle className={`h-4 w-4 flex-shrink-0 ${state.color}`} title={state.tooltip} />
                                ) : state.icon === 'loader' ? (
                                  <Loader2 className={`h-4 w-4 flex-shrink-0 animate-spin ${state.color}`} title={state.tooltip} />
                                ) : (
                                  <XCircle className={`h-4 w-4 flex-shrink-0 ${state.color}`} title={state.tooltip} />
                                )}
                                <span className="text-xs font-medium px-1.5 py-0.5 rounded" style={{backgroundColor: 'transparent'}}>
                                  <span className={state.color}>{state.label}</span>
                                </span>
                              </div>
                              <p className="text-sm font-medium mt-1 truncate">{entry.sent_to}</p>
                              {entry.cc && <p className="text-xs text-slate-500">CC: {entry.cc}</p>}
                              <p className="text-xs text-slate-400 mt-1">
                                {entry.delivered_at 
                                  ? new Date(entry.delivered_at).toLocaleString('pt-PT')
                                  : entry.sent_at 
                                    ? new Date(entry.sent_at).toLocaleString('pt-PT') 
                                    : new Date(entry.attempted_at).toLocaleString('pt-PT')
                                } · {entry.sent_by}
                              </p>
                              {entry.error && <p className="text-xs text-red-600 mt-1">{entry.error}</p>}
                              {entry.bounce_reason && <p className="text-xs text-red-600 mt-1">{entry.bounce_reason}</p>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              
              {/* Inbound Section */}
              <div>
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-2">
                  <Inbox className="h-3.5 w-3.5" />
                  Respostas Recebidas ({communicationData?.reply_count || 0})
                </h4>
                {(!communicationData?.inbound || communicationData.inbound.length === 0) ? (
                  <p className="text-sm text-slate-400 italic py-2">Nenhuma resposta recebida</p>
                ) : (
                  <div className="space-y-2">
                    {communicationData.inbound.map((entry, idx) => (
                      <div 
                        key={idx} 
                        className={`p-3 rounded border cursor-pointer transition-colors ${
                          entry.read_at 
                            ? 'border-slate-200 bg-white hover:bg-slate-50' 
                            : 'border-amber-300 bg-amber-50 hover:bg-amber-100'
                        }`}
                        onClick={() => handleOpenInbound(entry)}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              {entry.read_at ? (
                                <MailOpen className="h-4 w-4 text-slate-400 flex-shrink-0" />
                              ) : (
                                <Mail className="h-4 w-4 text-amber-600 flex-shrink-0" />
                              )}
                              <span className={`text-sm truncate ${entry.read_at ? 'text-slate-600' : 'font-semibold text-slate-800'}`}>
                                De: {entry.from_address}
                              </span>
                            </div>
                            <p className="text-xs text-slate-600 mt-1 truncate">{entry.subject}</p>
                            {entry.text_body && (
                              <p className="text-xs text-slate-400 mt-1 line-clamp-2">{entry.text_body.substring(0, 100)}...</p>
                            )}
                            <p className="text-xs text-slate-400 mt-1">
                              {new Date(entry.received_at).toLocaleString('pt-PT')}
                              {entry.read_at && (
                                <span className="ml-2 text-slate-400">· Lido por {entry.read_by}</span>
                              )}
                            </p>
                            {entry.attachments && entry.attachments.length > 0 && (
                              <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                                <Paperclip className="h-3 w-3" />
                                {entry.attachments.length} anexo{entry.attachments.length > 1 ? 's' : ''}
                              </p>
                            )}
                          </div>
                          <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Inbound Email Detail Modal */}
      <Dialog open={showInboundModal} onOpenChange={setShowInboundModal}>
        <DialogContent className="sm:max-w-[650px] bg-white max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Mail className="h-5 w-5 text-slate-600" />
              Resposta do Fornecedor
            </DialogTitle>
          </DialogHeader>
          
          {selectedInbound && (
            <div className="space-y-4 mt-2 overflow-y-auto flex-1">
              {/* Header Info */}
              <div className="space-y-2 text-sm border-b border-slate-200 pb-4">
                <div className="flex">
                  <span className="w-16 text-slate-500">De:</span>
                  <span className="font-medium">{selectedInbound.from_address}</span>
                </div>
                <div className="flex">
                  <span className="w-16 text-slate-500">Para:</span>
                  <span>{selectedInbound.to_addresses?.join(', ')}</span>
                </div>
                {selectedInbound.cc_addresses?.length > 0 && (
                  <div className="flex">
                    <span className="w-16 text-slate-500">CC:</span>
                    <span>{selectedInbound.cc_addresses.join(', ')}</span>
                  </div>
                )}
                <div className="flex">
                  <span className="w-16 text-slate-500">Assunto:</span>
                  <span className="font-medium">{selectedInbound.subject}</span>
                </div>
                <div className="flex">
                  <span className="w-16 text-slate-500">Data:</span>
                  <span>{new Date(selectedInbound.received_at).toLocaleString('pt-PT')}</span>
                </div>
                {selectedInbound.correlation?.matched && (
                  <div className="flex">
                    <span className="w-16 text-slate-500">Ref:</span>
                    <span className="text-blue-600">{selectedInbound.correlation.enf_reference || selectedInbound.correlation.po_number}</span>
                  </div>
                )}
              </div>
              
              {/* Body */}
              <div className="prose prose-sm max-w-none">
                {selectedInbound.text_body ? (
                  <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 bg-slate-50 p-4 rounded border border-slate-200">
                    {selectedInbound.text_body}
                  </pre>
                ) : selectedInbound.html_body ? (
                  <div 
                    className="bg-white p-4 rounded border border-slate-200 overflow-auto max-h-[300px]"
                    dangerouslySetInnerHTML={{ __html: selectedInbound.html_body.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') }}
                  />
                ) : (
                  <p className="text-slate-400 italic">Conteúdo não disponível</p>
                )}
              </div>
              
              {/* Attachments */}
              {selectedInbound.attachments && selectedInbound.attachments.length > 0 && (
                <div className="pt-4 border-t border-slate-200">
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Anexos</h4>
                  <div className="space-y-1">
                    {selectedInbound.attachments.map((att, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 p-2 rounded">
                        <Paperclip className="h-4 w-4 text-slate-400" />
                        <span>{att.filename || 'Anexo'}</span>
                        {att.size && <span className="text-xs text-slate-400">({Math.round(att.size / 1024)}KB)</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PDF Preview Modal */}
      <Dialog open={showPDFPreview} onOpenChange={closePDFPreview}>
        <DialogContent className="max-w-4xl h-[85vh] p-0 bg-white">
          <DialogHeader className="px-4 py-3 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-red-600" />
                Pré-visualização do PDF
              </DialogTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { closePDFPreview(); handleDownloadPDF(pdfPreviewPO); }}
                  className="h-8 text-xs"
                >
                  <FileDown className="h-3.5 w-3.5 mr-1.5" />
                  Descarregar
                </Button>
                <Button
                  size="sm"
                  onClick={() => { 
                    closePDFPreview(); 
                    const po = orders.find(o => o.po_number === pdfPreviewPO);
                    if (po) handleOpenEmailModal(po);
                  }}
                  className="h-8 text-xs bg-blue-600 hover:bg-blue-700"
                >
                  <Mail className="h-3.5 w-3.5 mr-1.5" />
                  Enviar por Email
                </Button>
              </div>
            </div>
          </DialogHeader>
          <div className="flex-1 h-full bg-slate-100">
            {pdfPreviewUrl ? (
              <iframe 
                src={pdfPreviewUrl} 
                className="w-full h-[calc(85vh-60px)]" 
                title="PDF Preview"
                data-testid="pdf-preview-iframe"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* SAGE Sync Result Modal */}
      <Dialog open={showSyncResultModal} onOpenChange={setShowSyncResultModal}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {syncResult?.sage_cancelled ? (
                <>
                  <Ban className="h-5 w-5 text-red-600" />
                  <span className="text-red-700">Encomenda Anulada no SAGE</span>
                </>
              ) : (
                <>
                  <RefreshCw className="h-5 w-5 text-cyan-600" />
                  Sincronização SAGE
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {syncResult?.sage_cancelled 
                ? 'A encomenda foi anulada directamente no SAGE'
                : 'Alterações detectadas na ENF'}
            </DialogDescription>
          </DialogHeader>
          {syncResult && (
            <div className="space-y-4">
              {/* CANCELLED STATE - Special display */}
              {syncResult.sage_cancelled ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                      <Ban className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="font-medium text-red-800">Encomenda Anulada</p>
                      <p className="text-sm text-red-600">
                        A ENF correspondente foi anulada no SAGE. O estado da encomenda foi atualizado para "Anulada" no sistema.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-cyan-800">
                      {syncResult.changes_count} alteração(ões) detetada(s)
                    </p>
                  </div>
                  
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {syncResult.changes?.map((change, idx) => (
                      <div 
                        key={idx} 
                        className={`p-3 rounded-lg border ${
                          change.type === 'removed' || change.type === 'po_cancelled' ? 'bg-red-50 border-red-200' :
                          change.type === 'added' ? 'bg-emerald-50 border-emerald-200' :
                          'bg-amber-50 border-amber-200'
                        }`}
                      >
                        {change.type === 'po_cancelled' ? (
                          <div className="flex items-center gap-2">
                            <Ban className="w-4 h-4 text-red-600" />
                            <span className="text-sm text-red-700">{change.description}</span>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-mono text-sm font-medium">{change.sku}</span>
                              <Badge 
                                variant="outline"
                                className={`text-[10px] ${
                                  change.type === 'removed' ? 'text-red-600 border-red-300' :
                                  change.type === 'added' ? 'text-emerald-600 border-emerald-300' :
                                  'text-amber-600 border-amber-300'
                                }`}
                              >
                                {change.type === 'removed' ? 'Removida' :
                                 change.type === 'added' ? 'Adicionada' :
                                 'Alterada'}
                              </Badge>
                            </div>
                            
                            {change.type === 'modified' && change.fields && (
                              <div className="text-xs text-slate-600 space-y-0.5">
                                {change.fields.map((f, fi) => (
                                  <div key={fi}>
                                    <span className="text-slate-500">{f.field === 'quantity' ? 'Quantidade' : f.field === 'unit_price' ? 'Preço' : f.field}:</span>
                                    {' '}<span className="line-through text-slate-400">{typeof f.from === 'number' ? f.from.toFixed(2) : f.from}</span>
                                    {' → '}<span className="font-medium">{typeof f.to === 'number' ? f.to.toFixed(2) : f.to}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {change.type === 'removed' && (
                              <div className="text-xs text-red-600">
                                Original: {change.original_quantity} un × €{change.original_price?.toFixed(2) || '0.00'}
                              </div>
                            )}
                            
                            {change.type === 'added' && (
                              <div className="text-xs text-emerald-600">
                                {change.quantity} un × €{change.unit_price?.toFixed(2) || '0.00'} = €{change.total?.toFixed(2) || '0.00'}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
              
              {syncResult.totals && !syncResult.sage_cancelled && (
                <div className="border-t border-slate-200 pt-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Total anterior:</span>
                    <span className="text-slate-600">€{syncResult.totals.before?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-medium">
                    <span className="text-slate-700">Total atual:</span>
                    <span className="text-slate-900">€{syncResult.totals.after?.toFixed(2)}</span>
                  </div>
                  {syncResult.totals.difference !== 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Diferença:</span>
                      <span className={syncResult.totals.difference > 0 ? 'text-emerald-600' : 'text-red-600'}>
                        {syncResult.totals.difference > 0 ? '+' : ''}€{syncResult.totals.difference?.toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSyncResultModal(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SAGE Sync History Modal */}
      <Dialog open={showSyncHistoryModal} onOpenChange={setShowSyncHistoryModal}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-slate-600" />
              Histórico de Sincronização SAGE
            </DialogTitle>
            <DialogDescription>
              {syncHistoryData?.po_number}
            </DialogDescription>
          </DialogHeader>
          
          {loadingSyncHistory ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : syncHistoryData?.history?.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <History className="h-8 w-8 mx-auto mb-2 text-slate-300" />
              <p>Sem histórico de sincronização</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {syncHistoryData?.history?.map((record, idx) => (
                <div key={idx} className="border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-slate-500">
                      {new Date(record.synced_at).toLocaleString('pt-PT')}
                    </span>
                    <span className="text-xs text-slate-400">
                      {record.synced_by}
                    </span>
                  </div>
                  
                  {record.has_changes ? (
                    <>
                      <p className="text-sm font-medium text-slate-700 mb-2">
                        {record.changes_count} alteração(ões)
                      </p>
                      <div className="space-y-1">
                        {record.changes?.slice(0, 3).map((change, ci) => (
                          <div key={ci} className="text-xs text-slate-600 bg-slate-50 rounded px-2 py-1">
                            <span className="font-mono">{change.sku}</span>
                            {' — '}
                            <span className={
                              change.type === 'removed' ? 'text-red-600' :
                              change.type === 'added' ? 'text-emerald-600' :
                              'text-amber-600'
                            }>
                              {change.type === 'removed' ? 'Removida' :
                               change.type === 'added' ? 'Adicionada' :
                               change.fields?.map(f => `${f.field}: ${f.from}→${f.to}`).join(', ')}
                            </span>
                          </div>
                        ))}
                        {record.changes?.length > 3 && (
                          <div className="text-xs text-slate-400 italic">
                            + {record.changes.length - 3} mais alterações
                          </div>
                        )}
                      </div>
                      {record.totals && (
                        <div className="mt-2 text-xs text-slate-500">
                          Total: €{record.totals.before?.toFixed(2)} → €{record.totals.after?.toFixed(2)}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-slate-500">Sem alterações detectadas</p>
                  )}
                </div>
              ))}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSyncHistoryModal(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Sync Pending Warning Dialog */}
      <Dialog open={showSyncPendingWarning} onOpenChange={setShowSyncPendingWarning}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Dados não atualizados do SAGE
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              Os dados desta encomenda ainda não foram atualizados a partir do SAGE após a integração.
            </p>
            <p className="text-sm text-slate-500">
              {syncPendingAction === 'email' 
                ? 'Se continuar, o email poderá conter valores desatualizados.'
                : 'Se continuar, o PDF poderá conter valores desatualizados.'}
            </p>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setShowSyncPendingWarning(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="default"
              onClick={handleSyncPendingSync}
              disabled={syncingFromSage[emailModalPO?.po_number]}
            >
              {syncingFromSage[emailModalPO?.po_number] ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />A sincronizar...</>
              ) : (
                <><RefreshCw className="h-4 w-4 mr-2" />Sincronizar agora</>
              )}
            </Button>
            <Button
              variant="ghost"
              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
              onClick={handleSyncPendingContinue}
            >
              Continuar mesmo assim
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseOrders;
